import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CardContent,
    Divider,
    Grid,
    Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { EnchancedModal } from 'src/components/Modals';
import {
    DateFilter,
    GridItem,
    MultiSelectFilter,
    SelectFilter,
    TextFieldFilter,
} from 'src/components/Search/components';
import type {
    TRangeValue,
    TSelectItem,
} from 'src/components/Search/components/types';
import { mapAgents } from 'src/components/Search/utils/mapAgents';
import { mapStores } from 'src/components/Search/utils/mapStores';
import { DEVELOPER_LEVEL_ACCESS } from 'src/components/access';
import useAuth from 'src/hooks/useAuth';
import useStore from 'src/hooks/useStore';
import useUser from 'src/hooks/useUser';
import {
    DEFAULT_CONVERSATION_ADVANCED_FILTERS,
    type TConversationAdvancedFilters,
    type TConversationStatusFilter,
} from '../../constants/conversationAdvancedFilters';
import {
    collectUniqueStoreTags,
    getAccessibleStoresForUser,
} from '../../utils/storeTags';
import {
    getOmnichannelAreaOptions,
    shouldShowOmnichannelAreaFilter,
} from '../../utils/omnichannelAreaFilter';

type TDateField = 'createdAt' | 'lastMessageAt';

const DATE_FIELDS: TDateField[] = ['createdAt', 'lastMessageAt'];

interface ConversationAdvancedFiltersProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    values: TConversationAdvancedFilters;
    onApply: (filters: TConversationAdvancedFilters) => void;
}

export const ConversationAdvancedFilters = ({
    open,
    setOpen,
    values,
    onApply,
}: ConversationAdvancedFiltersProps) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { stores, getStores } = useStore();
    const { agents, getAgents, clearUserState } = useUser();

    const [filters, setFilters] = useState<TConversationAdvancedFilters>(
        DEFAULT_CONVERSATION_ADVANCED_FILTERS
    );
    const [currentDateRange, setCurrentDateRange] =
        useState<TDateField>('createdAt');

    const accessibleStores = useMemo(
        () => getAccessibleStoresForUser(user, stores),
        [user, stores]
    );

    const availableAreas = useMemo(
        () => getOmnichannelAreaOptions(user),
        [user]
    );
    const showAreaFilter = shouldShowOmnichannelAreaFilter(availableAreas);

    const storeOptions = useMemo(
        () => mapStores(accessibleStores as any),
        [accessibleStores]
    );

    const areaOptions = useMemo(
        () =>
            availableAreas.map((area) => ({
                value: area,
                label: String(t(`Areas.${area}`, area)),
            })),
        [availableAreas, t]
    );

    const agentOptions = useMemo(() => mapAgents(agents || []), [agents]);

    const tagOptions = useMemo(() => {
        return collectUniqueStoreTags(accessibleStores).map((tag) => ({
            value: tag.name,
            label: tag.name,
            color: tag.color,
        }));
    }, [accessibleStores]);

    const statusOptions = useMemo(
        () => [
            { value: '', label: t('ModalFilters.allStatuses') },
            {
                value: 'active',
                label: t('Chat.ConversationStatusActive'),
            },
            {
                value: 'archived',
                label: t('Chat.ConversationStatusArchived'),
            },
            {
                value: 'blocked',
                label: t('Chat.ConversationStatusBlocked'),
            },
        ],
        [t]
    );

    const loadAgents = (selectedStores: TSelectItem[]) => {
        const storeIds =
            selectedStores.length > 0
                ? selectedStores.map((store) => store.value)
                : accessibleStores
                      .map((store: any) => store?._id || store?.id)
                      .filter(Boolean);

        if (!storeIds.length) {
            clearUserState();
            return;
        }

        getAgents({
            stores: storeIds,
            ...(user?.carType ? { carType: user.carType } : {}),
        });
    };

    useEffect(() => {
        if (!open) return;

        if (user?.accessLevel === DEVELOPER_LEVEL_ACCESS) {
            getStores();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, user?.accessLevel]);

    useEffect(() => {
        if (!open) return;

        setFilters({
            ...values,
            areas: (values.areas || []).filter((area) =>
                availableAreas.includes(area.value)
            ),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, availableAreas]);

    useEffect(() => {
        if (!open) return;
        loadAgents(filters.store);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.store, accessibleStores, open]);

    const handleInputChange =
        (field: 'name' | 'phone' | 'email') =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setFilters((prev) => ({
                ...prev,
                [field]: event.target.value,
            }));
        };

    const handleSelectChange =
        (field: 'store' | 'assignedTo' | 'tags' | 'areas') =>
        (selected: TSelectItem[] | unknown) => {
            const nextValue = Array.isArray(selected) ? [...selected] : [];

            setFilters((prev) => {
                if (field === 'store') {
                    return {
                        ...prev,
                        store: nextValue,
                        assignedTo: [],
                    };
                }

                return { ...prev, [field]: nextValue };
            });
        };

    const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilters((prev) => ({
            ...prev,
            status: event.target.value as TConversationStatusFilter,
        }));
    };

    const handleDateRangeChange =
        (field: TDateField) => (range: TRangeValue) => {
            setFilters((prev) => ({ ...prev, [field]: range }));
        };

    const handleClearDateRange = (field: TDateField) => () => {
        setFilters((prev) => ({ ...prev, [field]: null }));
    };

    const handleApply = () => {
        setOpen(false);
        onApply(filters);
    };

    const handleClear = () => {
        setOpen(false);
        setFilters(DEFAULT_CONVERSATION_ADVANCED_FILTERS);
        onApply(DEFAULT_CONVERSATION_ADVANCED_FILTERS);
    };

    return (
        <EnchancedModal
            open={open}
            setOpen={setOpen}
            title={t('Buttons.AdvancedSearch')}
            maxWidth="md"
        >
            <CardContent
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 15,
                }}
            >
                <Typography>{t('ModalFilters.filters')}</Typography>

                <Grid container spacing={2}>
                    <GridItem>
                        <TextFieldFilter
                            label={t('ModalFilters.name')}
                            value={filters.name}
                            onChange={handleInputChange('name')}
                            placeholder={t('ModalFilters.placeholder.name')}
                        />
                    </GridItem>

                    <GridItem>
                        <TextFieldFilter
                            label={t('ModalFilters.phone')}
                            value={filters.phone}
                            onChange={handleInputChange('phone')}
                            placeholder={t('ModalFilters.placeholder.phone')}
                        />
                    </GridItem>

                    <GridItem>
                        <TextFieldFilter
                            label={t('ModalFilters.email')}
                            value={filters.email}
                            onChange={handleInputChange('email')}
                            placeholder={t('ModalFilters.placeholder.email')}
                        />
                    </GridItem>

                    <GridItem>
                        <MultiSelectFilter
                            label={t('ModalFilters.store')}
                            options={storeOptions}
                            value={filters.store}
                            onChange={handleSelectChange('store')}
                            placeholder={t('ModalFilters.placeholder.store')}
                        />
                    </GridItem>

                    {showAreaFilter && (
                        <GridItem>
                            <MultiSelectFilter
                                label={t('Chat.FilterAreas')}
                                options={areaOptions}
                                value={filters.areas || []}
                                onChange={handleSelectChange('areas')}
                                placeholder={t('AdvancedAll.Areas')}
                            />
                        </GridItem>
                    )}

                    <GridItem>
                        <MultiSelectFilter
                            label={t('ModalFilters.agent')}
                            options={agentOptions}
                            value={filters.assignedTo}
                            onChange={handleSelectChange('assignedTo')}
                            placeholder={t('ModalFilters.placeholder.agent')}
                        />
                    </GridItem>

                    <GridItem>
                        <MultiSelectFilter
                            label={t('Stores.Tags.Title')}
                            options={tagOptions}
                            value={filters.tags}
                            onChange={handleSelectChange('tags')}
                            placeholder={t('Chat.FilterByTags')}
                        />
                    </GridItem>

                    <GridItem>
                        <SelectFilter
                            label={t('ModalFilters.status')}
                            value={filters.status}
                            onChange={handleStatusChange}
                            options={statusOptions}
                        />
                    </GridItem>
                </Grid>

                <Divider />

                <Typography>{t('ModalFilters.dateRanges')}</Typography>

                <Grid container spacing={2}>
                    {DATE_FIELDS.map((field) => (
                        <GridItem key={field}>
                            <DateFilter
                                field={field as any}
                                value={filters[field] ?? undefined}
                                setCurrentDateRange={setCurrentDateRange as any}
                                onClear={handleClearDateRange(field)}
                                isActive={currentDateRange === field}
                                handleDateRangeChange={
                                    handleDateRangeChange as any
                                }
                            />
                        </GridItem>
                    ))}
                </Grid>

                <Box
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '1em',
                        gap: 16,
                        flexWrap: 'wrap',
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleClear}
                        style={{ minHeight: 55, minWidth: 250 }}
                    >
                        {t('ModalFilters.clear')}
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleApply}
                        style={{ minHeight: 55, minWidth: 250 }}
                    >
                        {t('ModalFilters.apply')}
                    </Button>
                </Box>
            </CardContent>
        </EnchancedModal>
    );
};
